import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { setCookie, getCookie } from "@litres/utils";

import { isInvalidTokenCaptchaError } from "./types";
import type { AxiosBaseQueryArgs, AxiosBaseQueryError } from "../baseQuery";
import { requestRequiresCaptchaError } from "./errors";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

interface CaptchaCheckResponse {
  payload: {
    data: {
      token: string;
    };
  };
}

const RECAPTCHA_CHECK_TOKEN_NAME = "recaptcha_jwt_token";

async function captchaCheckRequest(
  recaptchaToken: string,
  baseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError>,
  api: any,
  extraOptions: any
) {
  const captchaRes = await baseQuery(
    {
      url: "/foundation/api/auth/captcha-check",
      method: "POST",
      body: { answer: recaptchaToken },
    },
    api,
    extraOptions
  );

  if (captchaRes.error) {
    throw requestRequiresCaptchaError(
      "BackendInteractionError",
      "API: /auth/captcha-check"
    );
  }

  const responseData: CaptchaCheckResponse =
    captchaRes.data as unknown as CaptchaCheckResponse;
  const token = responseData.payload?.data?.token;

  setCookie(RECAPTCHA_CHECK_TOKEN_NAME, token, {
    expires: new Date(Date.now() + 2 * 60_000), // 2 minutes
  });

  return token;
}

/**
 * Создает обработчик запросов с поддержкой ReCAPTCHA Enterprise для RTK Query.
 *
 * Функция оборачивает базовый axios-запрос и добавляет автоматическую обработку капчи:
 * - Автоматически добавляет токен капчи из куки в заголовок CAPTCHA-TOKEN
 * - При получении 429 с заголовком captcha-required или 400 с InvalidTokenCaptchaError:
 *   1. Запрашивает новый токен через grecaptcha.enterprise.execute
 *   2. Отправляет токен на /foundation/api/auth/captcha-check
 *   3. Сохраняет полученный JWT в куки на 2 минуты
 *   4. Повторяет исходный запрос с новым токеном
 *
 * @param {BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError>} baseQuery - Базовая функция запроса RTK Query
 * @param {string} siteKey - Ключ сайта reCAPTCHA Enterprise
 *
 * @returns {BaseQueryFn} Функция запроса с поддержкой капчи
 *
 * @throws {AxiosBaseQueryError} Если не смогли обработать флоу с получением рекапчи
 *
 * @example
 * const api = createApi({
 *   baseQuery: createCaptchaAxiosBaseQuery(
 *     axiosBaseQuery,
 *     process.env.RECAPTCHA_SITE_KEY
 *   ),
 *   endpoints: (builder) => ({...})
 * });
 */

export function createCaptchaAxiosBaseQuery(
  baseQuery: BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError>,
  siteKey: string
): BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError> {
  return async (args, api, extraOptions) => {
    const recaptchaJTWToken = getCookie(RECAPTCHA_CHECK_TOKEN_NAME);
    const finalArgs = {
      ...args,
      headers: {
        ...args.headers,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ...(recaptchaJTWToken ? { "CAPTCHA-TOKEN": recaptchaJTWToken } : {}),
      },
    };

    let result = await baseQuery(finalArgs, api, extraOptions);

    if (result.error) {
      const error = result.error;
      const headers = error.meta?.headers ?? {};

      if (
        (error?.status === 429 && headers["captcha-required"]) ||
        (error?.status === 400 && isInvalidTokenCaptchaError(error.data))
      ) {
        if (!window.grecaptcha?.enterprise?.execute) {
          requestRequiresCaptchaError(
            "FrontendLogicError",
            "ReCAPTCHA not initialized"
          ).emit();
          return result;
        }
        try {
          const recaptchaToken = await window.grecaptcha?.enterprise?.execute(
            siteKey,
            { action: args.url?.replace(/-/g, "_") ?? "default_action" }
          );

          const newToken = await captchaCheckRequest(
            String(recaptchaToken),
            baseQuery,
            api,
            extraOptions
          );

          result = await baseQuery(
            {
              ...args,
              headers: {
                ...args.headers,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                "CAPTCHA-TOKEN": newToken,
              },
            },
            api,
            extraOptions
          );
        } catch (captchaError) {
          requestRequiresCaptchaError(
            "FrontendLogicError",
            "Couldn't processe ReCAPTCHA flow validation"
          ).emit({
            error: captchaError,
            originalError: result.error,
          });
          return { error: captchaError as AxiosBaseQueryError };
        }
      }
    }

    return result;
  };
}
